import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { appLockApi } from '../../api/settings.api.js';
import { useLockStore } from '../../stores/lockStore.js';
import { LockScreen } from './LockScreen.js';

/**
 * Layer 2 — gates Settings behind the admin PIN. Even past the app-open lock
 * and the admin role, reaching Settings requires the separate admin PIN when
 * one is configured. Re-locks on reload (settingsUnlocked isn't persisted).
 */
export function AdminLockGate({ children }: { children: ReactNode }): JSX.Element {
  const { t } = useTranslation();
  const { data: status } = useQuery({
    queryKey: ['app-lock', 'status'],
    queryFn: appLockApi.status,
    staleTime: 60_000,
  });
  const settingsUnlocked = useLockStore((s) => s.settingsUnlocked);
  const unlockSettings = useLockStore((s) => s.unlockSettings);

  // Respect the single master toggle: the admin-PIN gate applies only when the
  // app lock is actually ENABLED (mirrors the open-lock in AppShell). Without
  // this, a *disabled* lock that still had an admin PIN set kept gating Settings
  // on the office web — where an admin already authenticated by login. The lock
  // is a shared-kiosk/tablet feature, not a second gate on the management console.
  if (status?.enabled && status?.hasAdminPin && !settingsUnlocked) {
    return (
      <LockScreen
        scope="admin"
        title={t('lock.admin.title', 'Admin PIN')}
        subtitle={t('lock.admin.subtitle', 'Kiosk Devices are protected — enter the admin PIN')}
        onUnlock={unlockSettings}
      />
    );
  }
  return <>{children}</>;
}
